export const FORFEO_QUERY_PERFORMANCE_VERSION = "4F-F3" as const;

export const FORFEO_QUERY_PERFORMANCE_POLICY = {
  defaultPageSize: 24,
  maximumPageSize: 100,
  maximumOffset: 10_000,
  maximumCacheTtlMs: 5 * 60 * 1000,
  maximumCacheEntries: 200,
  financialAuthority: "GIFTSHOP",
  financialResponsesCacheable: false,
  secretResponsesCacheable: false,
} as const;

export type BoundedPageRequest = {
  page?: number;
  pageSize?: number;
};

export type BoundedPage = {
  page: number;
  pageSize: number;
  from: number;
  to: number;
};

const finiteInteger = (value: number | undefined, fallback: number) => {
  if (!Number.isFinite(value)) return fallback;
  return Math.floor(value as number);
};

export function normalizeBoundedPage(request: BoundedPageRequest = {}): BoundedPage {
  const requestedSize = finiteInteger(request.pageSize, FORFEO_QUERY_PERFORMANCE_POLICY.defaultPageSize);
  const pageSize = Math.min(
    FORFEO_QUERY_PERFORMANCE_POLICY.maximumPageSize,
    Math.max(1, requestedSize),
  );
  const requestedPage = Math.max(1, finiteInteger(request.page, 1));
  const maximumPage = Math.floor(FORFEO_QUERY_PERFORMANCE_POLICY.maximumOffset / pageSize) + 1;
  const page = Math.min(requestedPage, maximumPage);
  const from = (page - 1) * pageSize;
  return { page, pageSize, from, to: from + pageSize - 1 };
}

export type CacheClassification = "public" | "operational-read";

export type BoundedCacheOptions = {
  classification: CacheClassification;
  ttlMs?: number;
  maximumEntries?: number;
  now?: () => number;
};

type CacheEntry<Value> = {
  expiresAt: number;
  value: Value;
};

export class BoundedTtlCache<Value> {
  readonly classification: CacheClassification;
  readonly ttlMs: number;
  readonly maximumEntries: number;
  readonly #now: () => number;
  readonly #entries = new Map<string, CacheEntry<Value>>();
  readonly #pending = new Map<string, Promise<Value>>();

  constructor(options: BoundedCacheOptions) {
    if (!new Set(["public", "operational-read"]).has(options.classification)) {
      throw new Error("Classification de cache interdite");
    }
    this.classification = options.classification;
    this.ttlMs = Math.min(
      FORFEO_QUERY_PERFORMANCE_POLICY.maximumCacheTtlMs,
      Math.max(1, finiteInteger(options.ttlMs, 60_000)),
    );
    this.maximumEntries = Math.min(
      FORFEO_QUERY_PERFORMANCE_POLICY.maximumCacheEntries,
      Math.max(1, finiteInteger(options.maximumEntries, 50)),
    );
    this.#now = options.now ?? Date.now;
  }

  get size() {
    this.#purgeExpired();
    return this.#entries.size;
  }

  get(key: string): Value | undefined {
    this.#validateKey(key);
    const entry = this.#entries.get(key);
    if (!entry) return undefined;
    if (entry.expiresAt <= this.#now()) {
      this.#entries.delete(key);
      return undefined;
    }
    this.#entries.delete(key);
    this.#entries.set(key, entry);
    return entry.value;
  }

  set(key: string, value: Value) {
    this.#validateKey(key);
    this.#purgeExpired();
    this.#entries.delete(key);
    while (this.#entries.size >= this.maximumEntries) {
      const oldest = this.#entries.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.#entries.delete(oldest);
    }
    this.#entries.set(key, { value, expiresAt: this.#now() + this.ttlMs });
    return value;
  }

  delete(key: string) {
    this.#validateKey(key);
    this.#pending.delete(key);
    return this.#entries.delete(key);
  }

  clear() {
    this.#entries.clear();
    this.#pending.clear();
  }

  async getOrLoad(key: string, loader: () => Promise<Value>): Promise<Value> {
    const cached = this.get(key);
    if (cached !== undefined) return cached;
    const existing = this.#pending.get(key);
    if (existing) return existing;
    const request = Promise.resolve()
      .then(loader)
      .then((value) => this.set(key, value))
      .finally(() => this.#pending.delete(key));
    this.#pending.set(key, request);
    return request;
  }

  #purgeExpired() {
    const now = this.#now();
    for (const [key, entry] of this.#entries) {
      if (entry.expiresAt <= now) this.#entries.delete(key);
    }
  }

  #validateKey(key: string) {
    if (typeof key !== "string" || key.length === 0 || key.length > 256) {
      throw new Error("Clé de cache invalide");
    }
  }
}
